import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress,
    TextField,
    Alert
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const INACTIVITY_TIMEOUT = 25 * 60 * 1000; // 25 minutes
const WARNING_DURATION = 10 * 60 * 1000; // 10 minutes
const WARNING_SECONDS = 600; // 10 minutes in seconds

/**
 * Component that handles user inactivity.
 * It shows a warning dialog after a period of inactivity and logs the user out if they remain inactive.
 *
 * @returns The Inactivity Logout Dialog UI (if warning is active)
 */
const InactivityLogout: React.FC = () => {
    const { user, logout, loginLogId } = useAuth();
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(WARNING_SECONDS);
    const [challengeCode, setChallengeCode] = useState<string | null>(null);
    const [userResponse, setUserResponse] = useState('');
    const [error, setError] = useState<string | null>(null);

    const inactivityTimerRef = useRef<number | null>(null);
    const warningTimerRef = useRef<number | null>(null);
    const countdownIntervalRef = useRef<number | null>(null);
    const lastActivityRef = useRef(Date.now());

    // Only apply to learners
    const isLearner = user?.role === 'learner';

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const fetchChallenge = async () => {
        if (!loginLogId) return;
        try {
            const api = (await import('../../services/api')).default;
            const res = await api.get(`/api/users/challenge?loginLogId=${loginLogId}`);
            if (res.data.challengeRequired) {
                setChallengeCode(res.data.challengeCode);
            } else {
                setChallengeCode(null);
            }
        } catch (err) {
            console.error("Error fetching anti-automation challenge", err);
        }
    };

    const resetInactivityTimer = () => {
        lastActivityRef.current = Date.now();

        // Clear existing inactivity timer
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        // Set new inactivity timer
        inactivityTimerRef.current = window.setTimeout(() => {
            // Show warning modal
            setShowWarning(true);
        }, INACTIVITY_TIMEOUT);
    };

    const handleAutoLogout = async () => {
        // Clear all timers
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

        // Logout
        await logout();
    };

    const handleContinue = async () => {
        if (challengeCode && !userResponse) {
            setError("Veuillez saisir le code de vérification");
            return;
        }

        // Send a heartbeat to backend to ensure session is kept alive there too
        try {
            const api = (await import('../../services/api')).default;
            await api.post('/api/users/heartbeat', {
                timeSpent: 1, // Minimal update to refresh lastActivity
                loginLogId: loginLogId,
                challengeResponse: userResponse
            });

            // If heartbeat successful, we can reset everything
            setShowWarning(false);
            setChallengeCode(null);
            setUserResponse('');
            setError(null);
            resetInactivityTimer();
        } catch (err: any) {
            console.error("Error sending heartbeat on stay", err);
            if (err.response?.status === 403 && err.response?.data?.challengeRequired) {
                setError("Code de vérification incorrect");
            } else {
                // Other error, maybe session expired on server
                handleAutoLogout();
            }
        }
    };

    // Effect to handle the warning countdown and auto-logout
    useEffect(() => {
        if (!showWarning) return;

        // Reset countdown to starting value
        setCountdown(WARNING_SECONDS);
        fetchChallenge();

        let secondsLeft = WARNING_SECONDS;
        const interval = window.setInterval(() => {
            secondsLeft--;
            if (secondsLeft >= 0) {
                setCountdown(secondsLeft);
            }

            if (secondsLeft <= 0) {
                handleAutoLogout();
            }
        }, 1000);

        const timeout = window.setTimeout(() => {
            handleAutoLogout();
        }, WARNING_DURATION);

        // Store in refs for handleAutoLogout and handleContinue access if needed
        countdownIntervalRef.current = interval;
        warningTimerRef.current = timeout;

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
            countdownIntervalRef.current = null;
            warningTimerRef.current = null;
        };
    }, [showWarning]);

    useEffect(() => {
        if (!isLearner) return;

        // Activity events to track
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

        const handleActivity = () => {
            // Only reset if not showing warning
            if (!showWarning) {
                resetInactivityTimer();
            }
        };

        // Add event listeners
        events.forEach(event => {
            window.addEventListener(event, handleActivity);
        });

        // Initialize timer
        if (!showWarning) {
            resetInactivityTimer();
        }

        // Cleanup
        return () => {
            events.forEach(event => {
                window.removeEventListener(event, handleActivity);
            });
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        };
    }, [isLearner, showWarning]);

    if (!isLearner) return null;

    return (
        <Dialog
            open={showWarning}
            onClose={() => { }} // Prevent closing by clicking outside
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, p: 1 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
                <Box
                    sx={{
                        bgcolor: 'warning.light',
                        color: 'warning.main',
                        p: 1,
                        borderRadius: 2,
                        display: 'flex'
                    }}
                >
                    <WarningIcon fontSize="large" />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Session inactive</Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Vous avez été inactif pendant 30 minutes. Pour votre sécurité, vous serez déconnecté automatiquement dans :
                </Typography>

                <Box
                    sx={{
                        p: 4,
                        bgcolor: '#fff5f5',
                        borderRadius: 4,
                        textAlign: 'center',
                        mb: 3,
                        border: '2px solid #fee2e2',
                        position: 'relative'
                    }}
                >
                    <Typography variant="h1" color="error" sx={{ fontWeight: 800, letterSpacing: -2 }}>
                        {formatTime(countdown)}
                    </Typography>
                </Box>

                {challengeCode && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 3, border: '1px solid', borderColor: 'primary.main' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <FingerprintIcon color="primary" />
                            <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 700 }}>
                                Vérification de présence
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Veuillez recopier le code suivant pour confirmer que vous êtes toujours là :
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    fontWeight: 800,
                                    fontSize: '1.2rem',
                                    letterSpacing: 4,
                                    border: '2px dashed',
                                    borderColor: 'primary.main',
                                    flex: 1,
                                    textAlign: 'center',
                                    userSelect: 'all'
                                }}
                            >
                                {challengeCode}
                            </Box>
                            <TextField
                                size="small"
                                placeholder="Entrez le code"
                                value={userResponse}
                                onChange={(e) => setUserResponse(e.target.value.toUpperCase())}
                                sx={{ flex: 1, bgcolor: 'white', borderRadius: 1 }}
                                autoComplete="off"
                                error={!!error}
                            />
                        </Box>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                <LinearProgress
                    variant="determinate"
                    value={(countdown / WARNING_SECONDS) * 100}
                    color="error"
                    sx={{ height: 10, borderRadius: 5, bgcolor: '#fee2e2' }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    Cliquez sur le bouton ci-dessous pour continuer votre session.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
                <Button
                    onClick={handleAutoLogout}
                    variant="text"
                    color="inherit"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Se déconnecter
                </Button>
                <Button
                    onClick={handleContinue}
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                    }}
                    autoFocus
                >
                    Continuer à étudier
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InactivityLogout;
